import { useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DeviceAutoLoginProps {
  children: ReactNode;
}

export function DeviceAutoLogin({ children }: DeviceAutoLoginProps) {
  const [state, setState] = useState<'checking' | 'error' | 'ready'>('checking');
  const [errorMsg, setErrorMsg] = useState('');
  const deviceTokenRef = useRef<string | null>(null);
  const viewTypeRef = useRef<string | null>(null);
  const hasTriedAutoLogin = useRef(false);

  const authenticateWithToken = async (token: string): Promise<{ success: boolean; viewType?: string; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('device-auth', {
        body: { token },
      });
      if (error) return { success: false, error: error.message };
      if (data?.error) return { success: false, error: data.error };
      if (!data?.access_token) return { success: false, error: 'No access token returned' };

      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (sessionError) return { success: false, error: sessionError.message };

      return { success: true, viewType: data.view_type };
    } catch (err: any) {
      return { success: false, error: err.message || 'Unknown error' };
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('device');

    if (!token) {
      setState('ready');
      return;
    }

    deviceTokenRef.current = token;

    const init = async () => {
      if (hasTriedAutoLogin.current) return;
      hasTriedAutoLogin.current = true;

      // Check if there's already an active session
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Session exists — autoRefreshToken handles renewal
        setState('ready');
        return;
      }

      // No session — authenticate
      const result = await authenticateWithToken(token);
      if (!result.success) {
        setErrorMsg(result.error || 'Authentication failed');
        setState('error');
        return;
      }

      viewTypeRef.current = result.viewType || null;

      // Navigate to the assigned view
      const targetPath = `/${result.viewType || 'kitchen'}`;
      // Clean up URL by removing the device param
      const url = new URL(window.location.href);
      url.searchParams.delete('device');
      window.history.replaceState({}, '', url.pathname + url.search);
      // Navigate
      window.location.href = targetPath;
    };

    init();
  }, []);

  if (state === 'checking') {
    // Check quickly if there's a device token
    const params = new URLSearchParams(window.location.search);
    if (!params.get('device')) {
      return <>{children}</>;
    }
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Authenticating device…</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2 p-6">
          <p className="text-lg font-semibold text-destructive">Device Authentication Failed</p>
          <p className="text-sm text-muted-foreground">{errorMsg}</p>
          <p className="text-xs text-muted-foreground mt-4">
            Contact your administrator if this problem persists.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
